import { FC, useEffect, useId, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Form } from 'antd';
import styled from 'styled-components';
import * as yup from 'yup';

import { makeEmail } from '@/_pages/inbox/_pageModule/hooks/useInboxConfig';
import { clientConfig } from '@/config/clientConfig';
import {
  createEmailPassword,
  generateRandomEmail,
  inboxEmailRe,
  USERNAME_MAX_LENGTH,
  USERNAME_MIN_LENGTH,
} from '@/features/inbox';
import { useNotices } from '@/features/notices/useNotices';
import { useApi } from '@/shared/api';
import { Input } from '@/shared/components';
import { COLORS } from '@/shared/constants/colors';
import { useDebounce } from '@/shared/hooks/use-debounce';
import { useProfileStore } from '@/shared/store/profile.store';
import { Icon } from '@/shared/uikit/Icon';
import { Title } from '@/shared/uikit/Title';
import { Button } from '@/shared/uikitV2/Button';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';
import { log } from '@/shared/utils/log';
import { copy } from '@/shared/utils/misc';

import { BtnClose } from '../../components/BtnClose';
import { ConfirmClose } from '../../components/ConfirmClose';
import { useModalsStore } from '../../modals.store';
import { InboxEmailConfigModalProps } from '../../types';

import { Actions, Body, Container, Header, StyledDrawer } from './styled';
import { useConfirmClose } from './useConfirmClose';

export type InboxEmailForm = {
  username: string;
};

export const InboxEmailConfigModal: FC<InboxEmailConfigModalProps> = ({ emailInfo, onSave }) => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'inboxEmailConfig' });
  const modals = useModalsStore();
  const profileId = useProfileStore((s) => s.profileId);
  const notices = useNotices();
  const api = useApi();

  const formId = useId();
  const [isFormDisabled, setIsFormDisabled] = useState(false);
  const [loading, setLoading] = useState(false);

  const [isAlreadyTaken, setIsAlreadyTaken] = useState<boolean | undefined>(undefined);
  const confirmClose = useConfirmClose();

  const { control, handleSubmit, watch, setValue } = useForm<InboxEmailForm>({
    defaultValues: {
      username: emailInfo ? emailInfo.username : '',
    },
    resolver: yupResolver(
      yup.object({
        username: yup
          .string()
          .trim()
          .required(t('validations.emailRequired'))
          .min(USERNAME_MIN_LENGTH, t('validations.minLength', { min: USERNAME_MIN_LENGTH }))
          .max(USERNAME_MAX_LENGTH, t('validations.maxLength', { max: USERNAME_MAX_LENGTH }))
          // eslint-disable-next-line security/detect-unsafe-regex
          .matches(inboxEmailRe, t('validations.invalidFormat')),
      }),
    ),
  });

  const usernameValue = watch('username');
  const debouncedUsername = useDebounce(usernameValue, 500);
  const validLength =
    usernameValue.length >= USERNAME_MIN_LENGTH && usernameValue.length <= USERNAME_MAX_LENGTH;

  const handleCopy = () => {
    copy(makeEmail({ username: usernameValue, domain: clientConfig.inboxEmailDomain! }));
    notices.success('Email copied', { key: 'inboxemailcopied' });
  };

  const onUserClose = () => {
    if (loading) {
      return;
    }

    if ((emailInfo?.username || usernameValue) && emailInfo?.username !== usernameValue) {
      if (!confirmClose.data) {
        confirmClose.confirm(modals.closeModal);
      }
    } else {
      modals.closeModal();
    }
  };

  const onSubmit: SubmitHandler<InboxEmailForm> = async (data) => {
    if (data.username === emailInfo?.username) {
      modals.closeModal();
      return;
    }

    setIsFormDisabled(true);
    setLoading(true);
    if (emailInfo?.username !== undefined && data.username !== emailInfo?.username) {
      const res = await api.mailServerDeleteAccount(emailInfo.username);

      if (res.error) {
        notices.error(res.error?.message);
        setLoading(false);
        return;
      }
    }

    const res = await api.mailServerCreateAccount({
      username: data.username!,
      password: createEmailPassword(),
      domain: clientConfig.inboxEmailDomain!,
      profileId: String(profileId),
    });

    if (res.error) {
      notices.error(res.error.message);
      log.error(res.error.message);
      setLoading(false);
      return;
    }

    notices.success('Email settings updated successfully!');
    modals.closeModal();
    onSave({ username: usernameValue, domain: clientConfig.inboxEmailDomain! });
    setIsFormDisabled(false);
    setLoading(false);
  };

  const onRemoveEmail = () => {
    const remove = async () => {
      confirmClose.hide();

      if (!emailInfo) {
        return;
      }

      setLoading(true);
      const res = await api.mailServerDeleteAccount(emailInfo.username);

      if (res.error) {
        notices.error(res.error.message);
        setLoading(false);
        return;
      }

      notices.success('Email settings updated successfully!');
      modals.closeModal();
      onSave({ username: usernameValue, domain: clientConfig.inboxEmailDomain! });
    };

    if (emailInfo?.username) {
      confirmClose.confirm(remove, 'Are you sure you want to delete the email?');
    } else {
      modals.closeModal();
      onSave({ username: usernameValue, domain: clientConfig.inboxEmailDomain! });
    }
  };

  useEffect(() => {
    const controller = new AbortController();

    const checkEmail = async () => {
      if (
        !debouncedUsername ||
        debouncedUsername.length < USERNAME_MIN_LENGTH ||
        debouncedUsername.length > USERNAME_MAX_LENGTH ||
        debouncedUsername === emailInfo?.username
      ) {
        setLoading(false);
        setIsAlreadyTaken(undefined);
        return;
      }

      setLoading(true);

      try {
        const { data: result, error } = await api.mailServerCheckAccountAvailability(
          {
            search: debouncedUsername,
          },
          { signal: controller.signal },
        );

        if (error && error.message && !result) {
          setIsAlreadyTaken(undefined);
          notices.error(t('errors.checkAccountAvailability', { message: error?.message }), {
            key: 'inbox-email-check-availability-error',
          });
          return;
        }

        setIsAlreadyTaken(result?.isTaken);
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') {
          return;
        }

        console.error(err);
      }

      setLoading(false);
    };

    checkEmail();

    return () => {
      controller.abort();
    };
  }, [debouncedUsername, notices, t, emailInfo?.username, api]);

  useEffect(() => {
    setIsAlreadyTaken(undefined);
  }, [usernameValue, emailInfo?.username]);

  return (
    <StyledDrawer
      width={600}
      footer={null}
      closable={false}
      onClose={onUserClose}
      maskClosable={!loading}
      keyboard={!loading}
      open
    >
      <Container>
        <Header>
          <Title size={22} bold>
            {t('title')}
          </Title>
          <BtnClose
            style={{
              position: 'absolute',
              right: 24,
              top: 24,
              opacity: loading ? 0.5 : 1,
              pointerEvents: loading ? 'none' : 'auto',
            }}
            onClick={onUserClose}
          />
        </Header>
        <Body className="uikitScrollbar">
          <div>
            <Title size={16} bold>
              {t('subtitle')}
            </Title>
            <span style={{ marginTop: 4, color: COLORS.systemDark200 }}>{t('description')}</span>
          </div>
          <Form
            style={{ display: 'contents' }}
            id={formId}
            onFinish={usernameValue ? handleSubmit(onSubmit) : onRemoveEmail}
          >
            <Controller
              name="username"
              control={control}
              render={(props) => (
                <Form.Item
                  help={
                    props.fieldState.error?.message ||
                    (validLength && isAlreadyTaken === true
                      ? t('validations.emailTaken')
                      : validLength && isAlreadyTaken === false
                      ? t('validations.emailFree')
                      : undefined)
                  }
                  validateStatus={
                    props.fieldState.invalid || (validLength && isAlreadyTaken === true)
                      ? 'error'
                      : validLength && isAlreadyTaken === false
                      ? 'success'
                      : undefined
                  }
                >
                  <Input
                    {...props.field}
                    disabled={isFormDisabled}
                    onChange={(e) => {
                      setLoading(true);
                      props.field.onChange(e);
                    }}
                    placeholder={t('emailPlaceholder')}
                    suffix={
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          color: COLORS.systemDark200,
                        }}
                      >
                        @{clientConfig.inboxEmailDomain}
                        <Icon
                          style={{ cursor: 'pointer' }}
                          name="copy-simple"
                          onClick={handleCopy}
                        />
                      </div>
                    }
                  />
                </Form.Item>
              )}
            />
          </Form>
          <div style={{ display: 'flex', gap: 12 }}>
            <BtnGenerate
              disabled={isFormDisabled}
              onClick={() => setValue('username', generateRandomEmail())}
            >
              {t('btnGenerate')}
            </BtnGenerate>
            <BtnDelete disabled={isFormDisabled} onClick={() => setValue('username', '')}>
              {t('btnDelete')}
            </BtnDelete>
          </div>
        </Body>
        <Actions>
          <Button type="text" onClick={onUserClose} disabled={loading}>
            {t('btnCancel')}
          </Button>
          <Button
            style={{ minWidth: 200 }}
            htmlType="submit"
            form={formId}
            loading={loading}
            disabled={isAlreadyTaken}
          >
            {t('btnSave')}
          </Button>
        </Actions>
        {confirmClose.data && (
          <ConfirmClose
            text={confirmClose.data.text}
            onClose={confirmClose.hide}
            onConfirm={confirmClose.data.callback}
          />
        )}
      </Container>
    </StyledDrawer>
  );
};

const StyledButton = styled.button`
  height: 32px;
  padding-inline: 12px;
  border: 0;
  border-radius: 8px;
  font-weight: 700;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    pointer-events: 'none';
    cursor: not-allowed;
  }
`;

const BtnGenerate = styled(StyledButton)`
  background: ${COLORS.systemSky100};
  color: ${COLORS.systemSky200};
`;

const BtnDelete = styled(StyledButton)`
  background: ${COLORS.systemFlame100};
  color: ${COLORS.systemFlame200};
`;
